import React, { useEffect, useMemo, useRef, useState } from 'react';
import './PreApprovalForm.scss';
import { TextInput, InputAutocompleteAddress } from '../../../../shared/components/ui/atoms';
import PasswordInput from '../../../../shared/components/ui/atoms/TextInput/PasswordInput';
import { FooterContent } from '../../../../features/onboarding/components';
import { OptionDropdownInner } from '../../../../shared/components/ui/atoms';
import { LoadingOutlined } from '@ant-design/icons';
import { Spin } from 'antd';
import { SPECIAL_CHARACTERS_REGEX, ONBOARDING_STEPS, getDisabledStateCodes } from '../../../../utils/constants';
import { SendLoanFormSubmitted } from '../../../../features/onboarding/components/SendLoanFormSubmitted/SendLoanFormSubmitted';
import { sendLeadData } from '../../../../features/onboarding/components/SendLoanForm/SendLoanForm';
import { OptionDropdown, DropdownOwnerships } from '../../../../shared/components/ui/atoms';
import { useOwnership } from '../../../onboarding/context/OwnershipContext';
import { useCheckBorrowerEmailExists } from '../../../broker/hooks/useBrokerQueries';
import { useAuth } from '../../../../features/auth/context/AuthContext';
import { BorrowerData, Ownership, FormValues } from '../../../../types';
import { useLogin } from '../../../../features/auth/hooks/useAuthQueries';
import { onboardingService } from '../../../../features/onboarding/services/onboarding.service';
import { formatAddressWithoutCountry } from '../../../../utils/functions';
import { BORROWER_DASHBOARD_URL } from '@common/config/environments';

/* Utility Functions */
// Function to format phone number
export const formatPhoneNumber = (phoneNumber: string) => {
  // Remove all non-digit characters
  let digits = phoneNumber.replace(/\D/g, '');

  if (digits.length === 0) {
    return '';
  }

  // Limit to 10 digits
  digits = digits.substring(0, 10);

  // Format as (123) 456-7890
  const formatted = `(${digits.substring(0, 3)}) ${digits.substring(3, 6)}-${digits.substring(6)}`;

  return formatted;
}

// Function to validate phone number
export const validatePhoneNumber = (phoneNumber: string) => {
  const digits = phoneNumber.replace(/\D/g, '');
  if (digits.length === 0) return "Phone number is required";
  if (digits.length < 10) return "Phone number must have 10 digits";
  return null;
}

/* Spinners */
const CustomSpinner = ({ color }: { color: string }) => (
  <Spin
    indicator={
      <LoadingOutlined
        style={{
          fontSize: 14,
          color: color,
          position: "relative",
          bottom: "1px"
        }}
        spin
      />
    }
  />
);

const WhiteSpinner = () => <CustomSpinner color="white" />;
const BlueSpinner = () => <CustomSpinner color="#162E43" />;

/* Custom Hook for Mobile Detection */
const useIsMobile = (breakpoint: number = 768): boolean => {
  const [isMobile, setIsMobile] = useState<boolean>(
    typeof window !== 'undefined' ? window.innerWidth < breakpoint : false
  );

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < breakpoint);
    };

    window.addEventListener('resize', handleResize);
    handleResize();

    return () => window.removeEventListener('resize', handleResize);
  }, [breakpoint]);

  return isMobile;
};

/* Components */
interface PreApprovalFormProps {
  values: FormValues;
  onContinue: (data: any) => void;
  borrowerData?: BorrowerData;
  ownerships?: Ownership[];
  isBrokerMode?: boolean;
  setValue: (key: string, value: any) => void;
  setSelectedIndex: (index: number, options?: { force?: boolean }) => void;
}

export const PreApprovalForm = (props: PreApprovalFormProps) => {
  const { ownershipSelected, setOwnershipSelected } = useOwnership();
  const isSomeoneElse = ownershipSelected?.isNew === true;
  const { values, onContinue, borrowerData, ownerships, isBrokerMode, setValue, setSelectedIndex } = props;

  // Detect if device is mobile
  const isMobile = useIsMobile();

  const [lockExistingBorrowerFlow, setLockExistingBorrowerFlow] = useState(false)
  const isProcessing = Boolean(borrowerData) && !lockExistingBorrowerFlow
  const hidePasswordFields = isProcessing;

  const [firstName, setFirstName] = useState(ownershipSelected?.name || "")
  const [lastName, setLastName] = useState(ownershipSelected?.lastname || "")
  const [email, setEmail] = useState(isSomeoneElse ? borrowerData?.email : ownershipSelected?.email || "")
  const [phoneNumber, setPhoneNumber] = useState(isSomeoneElse ? borrowerData?.phone : ownershipSelected?.phone || "")
  const [password, setPassword] = useState(hidePasswordFields ? "password" : "")
  const [passwordConfirmation, setPasswordConfirmation] = useState(hidePasswordFields ? "password" : "")
  const [investmentAddress, setInvestmentAddress] = useState("")
  const [investmentAddressFormatted, setInvestmentAddressFormatted] = useState('')
  const [isInvestmentAddressPrefilled, setIsInvestmentAddressPrefilled] = useState(false)
  const [hasSelectedInvestmentAddress, setHasSelectedInvestmentAddress] = useState(false)
  const [hasManuallyEditedInvestmentAddress, setHasManuallyEditedInvestmentAddress] = useState(false)
  const hasMarkedPrefill = useRef(false)
  const previousInvestmentAddressFromValues = useRef<string | number | undefined>(undefined)

  const [isLoading, setIsLoading] = useState(false)
  const [emailValidationLoading, setEmailValidationLoading] = useState(false)

  const [errors, setErrors] = useState<Record<string, string | null>>({});

  // Email validation hook
  const { checkEmail, emailExists, loading: emailCheckLoading } = useCheckBorrowerEmailExists();

  // Reset email validation when email changes
  const [emailExistsState, setEmailExistsState] = useState(false);
  const [addressSelected, setAddressSelected] = useState(false)
  const [identitySelection, setIdentitySelection] = useState<string>('a new borrower')
  const isExistingBorrower = identitySelection === 'an existing borrower'

  const shouldHidePasswordFields = hidePasswordFields || addressSelected;

  // Step flow state: step 1 (address) -> step 2 (full form)
  const [isStep2, setIsStep2] = useState(false);
  // Track if we should show validation errors (only after user tries to submit or interacts)
  const [shouldShowErrors, setShouldShowErrors] = useState(false);

  const buildNormalizedUserData = (ownershipOverride?: Ownership | null) => {
    const ownershipFallback = ownershipOverride || ownershipSelected || ownerships?.[0];
    const borrowerNameString = typeof borrowerData?.name === 'string' ? borrowerData.name.trim() : '';
    const borrowerNameParts = borrowerNameString.length > 0 ? borrowerNameString.split(' ') : [];
    const borrowerFirstName = borrowerNameParts[0] || '';
    const borrowerLastName = borrowerData?.lastName?.trim() || borrowerNameParts.slice(1).join(' ') || '';
    const borrowerPhone = typeof borrowerData?.phone === 'string' ? borrowerData.phone.trim() : '';

    const normalizedFirstName =
      (typeof firstName === 'string' && firstName.trim()) ||
      (typeof ownershipFallback?.name === 'string' ? ownershipFallback.name.trim() : '') ||
      borrowerFirstName;

    const normalizedLastName =
      (typeof lastName === 'string' && lastName.trim()) ||
      (typeof ownershipFallback?.lastname === 'string' ? ownershipFallback.lastname.trim() : '') ||
      borrowerLastName;

    const normalizedPhone =
      (typeof phoneNumber === 'string' && phoneNumber.trim()) ||
      (typeof ownershipFallback?.phone === 'string' ? ownershipFallback.phone.trim() : '') ||
      borrowerPhone;

    return {
      firstName: normalizedFirstName,
      lastName: normalizedLastName,
      email: typeof email === 'string' ? email.trim().toLowerCase() : email,
      phoneNumber: normalizedPhone,
      password,
      passwordConfirmation,
      investmentAddress: investmentAddressFormatted || investmentAddress
    };
  };

  const userData = buildNormalizedUserData();

  useEffect(() => {
    if (ownershipSelected) {
      setFirstName(isSomeoneElse ? (values as any)?.guarantorFirstName || '' : ownershipSelected.name || "");
      setLastName(isSomeoneElse ? (values as any)?.guarantorLastName || '' : ownershipSelected.lastname || "");
      setEmail(isSomeoneElse ? borrowerData?.email : ownershipSelected.email || "");
      setPhoneNumber(isSomeoneElse ? borrowerData?.phone : ownershipSelected.phone || "");
    }
  }, [ownershipSelected]);

  useEffect(() => {
    if (!Array.isArray(ownerships) || ownerships.length === 0) {
      return;
    }

    if (!ownershipSelected && identitySelection === 'an existing borrower') {
      setOwnershipSelected(ownerships[0]);
      return;
    }

    if (!ownershipSelected) {
      return;
    }

    const matchingOwnership = ownerships.find((item) => item._id === ownershipSelected._id);
    if (matchingOwnership && matchingOwnership !== ownershipSelected) {
      setOwnershipSelected(matchingOwnership);
    }
  }, [identitySelection, ownerships, ownershipSelected, setOwnershipSelected]);

  // Keep first/last in sync if guarantor names arrive later in values
  useEffect(() => {
    if (isSomeoneElse) {
      const gFirst = (values as any)?.guarantorFirstName;
      const gLast = (values as any)?.guarantorLastName;
      if (typeof gFirst === 'string' && gFirst && firstName !== gFirst) setFirstName(gFirst);
      if (typeof gLast === 'string' && gLast && lastName !== gLast) setLastName(gLast);
    }
  }, [values, isSomeoneElse]);

  // Initialize investmentAddress from values if available (on mount and when values change)
  useEffect(() => {
    const addressFromValues = values['Investment property address'];
    if (
      !hasManuallyEditedInvestmentAddress &&
      addressFromValues &&
      typeof addressFromValues === 'string' &&
      addressFromValues !== previousInvestmentAddressFromValues.current
    ) {
      setInvestmentAddress(addressFromValues);
      setHasSelectedInvestmentAddress(true);
      setHasManuallyEditedInvestmentAddress(false);
    }
    previousInvestmentAddressFromValues.current = addressFromValues;
  }, [values['Investment property address'], hasManuallyEditedInvestmentAddress]);

  useEffect(() => {
    if (!borrowerData || hasMarkedPrefill.current) return;
    const prefilledAddress =
      borrowerData?.investmentAddress ??
      borrowerData?.propertyAddress ??
      borrowerData?.address ??
      (typeof values['Investment property address'] === 'string' ? values['Investment property address'] : undefined);

    if (typeof prefilledAddress === 'string' && prefilledAddress.trim().length > 0) {
      setInvestmentAddress(prefilledAddress);
      setHasSelectedInvestmentAddress(true);
      setHasManuallyEditedInvestmentAddress(false);
      setIsInvestmentAddressPrefilled(true);
      hasMarkedPrefill.current = true;
    }
  }, [borrowerData, values]);

  const isFormComplete = () => {
    return Object.values(userData).every(value => value !== undefined && value !== '');
  }

  // Password validation functions
  const validatePasswordRequirements = (password: string) => {
    const requirements = [
      { label: "Lowercase letter", validator: (pwd: string) => /[a-z]/.test(pwd) },
      { label: "Uppercase letter", validator: (pwd: string) => /[A-Z]/.test(pwd) },
      { label: "Symbol", validator: (pwd: string) => SPECIAL_CHARACTERS_REGEX.test(pwd) },
      { label: "8 characters", validator: (pwd: string) => pwd.length >= 8 }
    ];

    return requirements.map(req => ({
      ...req,
      met: req.validator(password)
    }));
  };

  const isPasswordValid = (password: string) => {
    const requirements = validatePasswordRequirements(password);
    return requirements.every(req => req.met);
  };

  // Function to validate email existence
  const validateEmailExistence = async (emailValue: string) => {
    if (!emailValue || !/\S+@\S+\.\S+/.test(emailValue)) {
      setEmailExistsState(false);
      return null; // Don't check if email is invalid
    }

    setEmailValidationLoading(true);
    try {
      const result = await checkEmail({ variables: { email: emailValue } });
      if (result.data) {
        const emailExists = result.data.checkBorrowerEmailExists;
        setEmailExistsState(emailExists);
      }
    } catch (error) {
    } finally {
      setEmailValidationLoading(false);
    }
  };

  const validateInput = (label: string, value: string) => {
    switch (label) {
      case "First name":
      case "Last name":
        return value.trim() === "" ? "This field is required" : null;
      case "Investment Address":
        if (value.trim() === "") return "We need this to generate your pre-approval letter and term sheet";
        // If address is prefilled, don't require selection
        if (isInvestmentAddressPrefilled) return null;
        // If address was selected from dropdown, it's valid
        if (hasSelectedInvestmentAddress) return null;
        // If addressSelected is true (user chose "I don't have a specific property"), don't require selection
        if (addressSelected) return null;
        // Otherwise, require selection from dropdown
        return "Please select an address from the suggestions";
      case "Email address":
        const emailRegex = /\S+@\S+\.\S+/;
        if (!emailRegex.test(value)) return "Invalid email address";
        if (!isExistingBorrower && emailExistsState && !emailValidationLoading) return "This email is already registered";
        return null;
      case "Phone number":
        if (value.trim() === "") return "This field is required";
        const digits = value.replace(/\D/g, '');
        if (digits.length < 10) return "Phone number must have 10 digits";
        return null;
      case "Password":
        // Skip password validation if fields are hidden (user is logged in)
        if (shouldHidePasswordFields) return null;
        if (value.length === 0) return "Password is required";
        if (!isPasswordValid(value)) return "Password does not meet requirements";
        return null;
      case "Confirm Password":
        // Skip password confirmation validation if fields are hidden (user is logged in)
        if (shouldHidePasswordFields) return null;
        if (!password || !value) return "Please confirm your password";
        if (value !== password) return "Passwords do not match";
        return null;
      default:
        return null;
    }
  };

  const computedErrors = useMemo(() => {
    const isExistingBorrower = identitySelection === 'an existing borrower';
    // If we're in step 2 and address was selected/prefilled, don't show error for investmentAddress
    const shouldSkipInvestmentAddressError = isStep2 && (hasSelectedInvestmentAddress || isInvestmentAddressPrefilled);

    // If we're in step 2 and haven't tried to submit yet, don't show errors for empty fields
    const shouldSkipStep2Errors = isStep2 && !shouldShowErrors;

    return addressSelected
      ? {
        firstName: null,
        lastName: null,
        email: validateInput("Email address", email || ""),
        phoneNumber: null,
        password: null,
        passwordConfirmation: null,
        investmentAddress: null,
      }
      : (
        isExistingBorrower
          ? {
            firstName: null,
            lastName: null,
            email: validateInput("Email address", email || ""),
            phoneNumber: null,
            // For login, only require non-empty password (no strength rules)
            password: shouldSkipStep2Errors ? null : (password && password.length > 0 ? null : 'Password is required'),
            passwordConfirmation: null,
            investmentAddress: shouldSkipInvestmentAddressError ? null : validateInput("Investment Address", investmentAddress),
          }
          : {
            firstName: shouldSkipStep2Errors ? null : validateInput("First name", firstName),
            lastName: shouldSkipStep2Errors ? null : validateInput("Last name", lastName),
            email: validateInput("Email address", email || ""),
            phoneNumber: shouldSkipStep2Errors ? null : validateInput("Phone number", phoneNumber),
            password: shouldHidePasswordFields ? null : (shouldSkipStep2Errors ? null : validateInput("Password", password)),
            passwordConfirmation: shouldHidePasswordFields ? null : (shouldSkipStep2Errors ? null : validateInput("Confirm Password", passwordConfirmation)),
            investmentAddress: shouldSkipInvestmentAddressError ? null : validateInput("Investment Address", investmentAddress),
          }
      );
  }, [
    addressSelected,
    identitySelection,
    email,
    password,
    passwordConfirmation,
    investmentAddress,
    hasSelectedInvestmentAddress,
    isInvestmentAddressPrefilled,
    borrowerData,
    firstName,
    lastName,
    phoneNumber,
    shouldHidePasswordFields,
    emailExistsState,
    emailValidationLoading,
    isStep2,
    shouldShowErrors,
  ]);

  const checkErrors = (forceShowErrors = false) => {
    // If forceShowErrors is true, calculate errors as if shouldShowErrors is true
    if (forceShowErrors) {
      const isExistingBorrower = identitySelection === 'an existing borrower';
      const shouldSkipInvestmentAddressError = isStep2 && (hasSelectedInvestmentAddress || isInvestmentAddressPrefilled);

      const forcedErrors = addressSelected
        ? {
          firstName: null,
          lastName: null,
          email: validateInput("Email address", email || ""),
          phoneNumber: null,
          password: null,
          passwordConfirmation: null,
          investmentAddress: null,
        }
        : (
          isExistingBorrower
            ? {
              firstName: null,
              lastName: null,
              email: validateInput("Email address", email || ""),
              phoneNumber: null,
              password: password && password.length > 0 ? null : 'Password is required',
              passwordConfirmation: null,
              investmentAddress: shouldSkipInvestmentAddressError ? null : validateInput("Investment Address", investmentAddress),
            }
            : {
              firstName: validateInput("First name", firstName),
              lastName: validateInput("Last name", lastName),
              email: validateInput("Email address", email || ""),
              phoneNumber: validateInput("Phone number", phoneNumber),
              password: shouldHidePasswordFields ? null : validateInput("Password", password),
              passwordConfirmation: shouldHidePasswordFields ? null : validateInput("Confirm Password", passwordConfirmation),
              investmentAddress: shouldSkipInvestmentAddressError ? null : validateInput("Investment Address", investmentAddress),
            }
        );
      setErrors(forcedErrors);
      return forcedErrors;
    }

    setErrors(computedErrors);
    return computedErrors;
  }

  useEffect(() => {
    if (Object.keys(errors).length > 0) {
      checkErrors();
    }
  }, [firstName, lastName, email, phoneNumber, ...(hidePasswordFields ? [] : [password, passwordConfirmation]), investmentAddress, hasSelectedInvestmentAddress, hasManuallyEditedInvestmentAddress, isInvestmentAddressPrefilled, emailExistsState, emailValidationLoading])

  // Validate email existence when email changes
  useEffect(() => {
    if (email && !borrowerData && !isExistingBorrower) {
      const timeoutId = setTimeout(() => {
        validateEmailExistence(email.trim().toLowerCase());
      }, 1000); // Debounce for 1 second

      return () => clearTimeout(timeoutId);
    } else {
      // Clear email validation state when email is empty or borrowerData exists
      setEmailValidationLoading(false);
      setEmailExistsState(false);
    }
  }, [email, borrowerData, isExistingBorrower]);


  const [showConfirmationModal, setShowConfirmationModal] = useState(false)

  const { login: storeTokenAndRefresh, isAuthenticated } = useAuth();
  const { login: loginMutation } = useLogin();

  const handleContinue = async (isKeepMeInTheLoop?: boolean) => {
    // Enable error display when user tries to submit
    setShouldShowErrors(true);

    // Force check errors with shouldShowErrors = true
    const errors = checkErrors(true);
    const hasErrors = Object.values(errors).some(error => error !== null);

    // If "Keep me in the loop" is selected and address is selected, create a lead
    // This should happen regardless of whether it's "a new borrower" or "an existing borrower"
    if (isKeepMeInTheLoop === true && addressSelected) {
      const emailError = errors.email;
      const hasErrors = Boolean(emailError);
      const isFormComplete = Boolean(email && email.trim() !== '');

      if (isFormComplete && !hasErrors) {
        const leadData = { email } as any;
        sendLeadData({
          name: `${firstName || ''} ${lastName || ''}`.trim(),
          ...leadData,
          ...values
        })
        setShowConfirmationModal(true)
      }
      return;
    }

    // Existing borrower login flow (only if not in "keep me in the loop" mode)
    if (identitySelection === 'an existing borrower') {
      if (errors.email || errors.password) return;
      try {
        setIsLoading(true);
        setLockExistingBorrowerFlow(true);
        const result = await loginMutation({ variables: { loginUserInput: { email: email?.trim().toLowerCase(), password } } });
        const { data, errors: graphQLErrors } = result;

        // Check for GraphQL errors (with errorPolicy: 'all', errors don't throw exceptions)
        if (graphQLErrors && graphQLErrors.length > 0) {
          // Check if it's an authentication error from login
          const isLoginError = graphQLErrors.some(err =>
            err.path && Array.isArray(err.path) && err.path.includes('login')
          );
          if (isLoginError) {
            setErrors((prev) => ({ ...prev, password: 'Invalid username or password. Please check your credentials and try again.' }));
            setLockExistingBorrowerFlow(false);
            setIsLoading(false);
            return;
          }
        }

        // Check if data is null or token is missing (indicates error)
        if (!data || !data.login || !data.login.authToken) {
          setErrors((prev) => ({ ...prev, password: 'Invalid username or password. Please check your credentials and try again.' }));
          setLockExistingBorrowerFlow(false);
          setIsLoading(false);
          return;
        }

        const token = data.login.authToken;
        await storeTokenAndRefresh(token);
        const loginUser = data.login.user;
        const ownerId = loginUser?._id;
        let ownershipForSubmission: Ownership | null = null;

        if (ownerId) {
          try {
            const ownershipsResponse = await onboardingService.getOwnershipsByCreator(ownerId);
            if (Array.isArray(ownershipsResponse) && ownershipsResponse.length > 0) {
              const [firstOwnership] = ownershipsResponse;
              ownershipForSubmission = firstOwnership;

              if (!ownershipSelected) {
                setOwnershipSelected(firstOwnership);
              }

              setFirstName((prev: string) => {
                const trimmed = prev.trim();
                return trimmed.length > 0 ? prev : (firstOwnership?.name ?? '').trim();
              });
              setLastName((prev: string) => {
                const trimmed = prev.trim();
                return trimmed.length > 0 ? prev : (firstOwnership?.lastname ?? '').trim();
              });
              setPhoneNumber((prev: string) => {
                const trimmed = prev.replace(/\D/g, '');
                if (trimmed.length > 0) {
                  return prev;
                }
                const ownershipPhone = typeof firstOwnership?.phone === 'string' ? firstOwnership.phone : '';
                return ownershipPhone ? formatPhoneNumber(ownershipPhone) : '';
              });
            }
          } catch (ownershipError) {
            console.error('Error fetching ownerships after login:', ownershipError);
          }
        }
        const submissionUserData = buildNormalizedUserData(ownershipForSubmission);
        // After successful login, mimic the logged-in submit flow without swapping modal
        const invAddrError = validateInput("Investment Address", investmentAddress || "");
        if (!invAddrError) {
          onContinue({ ...submissionUserData, password: "", passwordConfirmation: "" });
        } else {
          setErrors((prev) => ({ ...prev, investmentAddress: invAddrError }));
        }
      } catch (e) {
        setErrors((prev) => ({ ...prev, password: 'Invalid username or password. Please check your credentials and try again.' }));
        setLockExistingBorrowerFlow(false); // Reset lock on error so user can try again
      } finally {
        setIsLoading(false);
      }
      return;
    }

    if (isKeepMeInTheLoop === true) {
      // Original logic for when address is not selected
      const { investmentAddress, ...errs } = errors
      const { investmentAddress: _, ...ud } = userData

      const isFormComplete = Object.values(ud).every(value => value !== undefined && value !== '');
      const hasErrors = Object.values(errs).some(error => error !== null);

      if (isFormComplete && !hasErrors) {
        const { password, passwordConfirmation, ...leadData } = userData
        sendLeadData({
          name: `${leadData['firstName']} ${leadData['lastName']}`,
          ...leadData,
          ...values
        })
        setShowConfirmationModal(true)
      }

      return
    }

    // If address is selected (I don't have specific property in mind), skip password validation
    if (addressSelected) {
      const { password, passwordConfirmation, investmentAddress, ...errs } = errors;
      const { password: _, passwordConfirmation: __, investmentAddress: ___, ...ud } = userData;

      const isFormComplete = Object.values(ud).every(value => value !== undefined && value !== '');
      const hasErrors = Object.values(errs).some(error => error !== null);

      if (isFormComplete && !hasErrors) {
        setIsLoading(true)
        setTimeout(() => {
          setIsLoading(false)
          onContinue({ ...userData, password: "", passwordConfirmation: "" });
        }, 1000)
      }
      return;
    }

    if (isFormComplete() && !hasErrors) {

      setIsLoading(true)
      setTimeout(() => {
        setIsLoading(false)
        onContinue(userData);
      }, 1000)
    }
  }

  const hasComputedErrors = Object.values(computedErrors).some(error => error !== null);
  const hasValidInvestmentAddressSelection =
    addressSelected || hasSelectedInvestmentAddress || isInvestmentAddressPrefilled;
  // If user manually typed in address (not selected from dropdown), disable the button
  // This checks if there's text in the input but it wasn't selected from the dropdown
  const hasManuallyTypedAddress =
    investmentAddress.trim().length > 0 &&
    !hasSelectedInvestmentAddress &&
    !isInvestmentAddressPrefilled &&
    !addressSelected;
  const shouldDisableContinueButton = hasComputedErrors || !hasValidInvestmentAddressSelection || hasManuallyTypedAddress;

  const options = [
    {
      value: "a new borrower",
      label: "a new borrower",
      disabled: false,
      className: undefined
    },
    {
      value: "an existing borrower",
      label: "an existing borrower",
      disabled: false,
      className: undefined
    },
  ]

  const w = (f: (value: any) => void) => (a: any) => { if (!borrowerData || !ownershipSelected || isSomeoneElse) f(a) }

  const title = borrowerData ? "Get your term sheet and pre-approval letter" : "Get your term sheet and pre-approval letter"
  const button = (isStep2 && identitySelection === 'an existing borrower' && !borrowerData) ? 'Continue loan application' : 'Continue loan application'

  useEffect(() => {
    if (investmentAddress) {
      setAddressSelected(false)
    }
  }, [investmentAddress])

  useEffect(() => {
    if (addressSelected) {
      setIsStep2(false)
    }
  }, [addressSelected])

  // Clear all errors when moving to step 2
  useEffect(() => {
    if (isStep2) {
      // Clear all errors and reset shouldShowErrors when moving to step 2
      setErrors({});
      setShouldShowErrors(false);
    }
  }, [isStep2])

  if (showConfirmationModal) {
    return (
      <SendLoanFormSubmitted isWhite={true} />
    )
  }

  const Wrapper = (
    (props: any) => <OptionDropdown {...props} isBrokerMode={isBrokerMode} values={values} setValue={setValue} setSelectedIndex={setSelectedIndex} parentClassName={`option-custom-${props.index}`} />
  )

  const ficoRange = (
    <Wrapper
      isSet={values[ONBOARDING_STEPS[5].text]}
      index={5}
      isDisabled
    />
  )

  const propertiesNegotiated = (
    <Wrapper
      isSet={values[ONBOARDING_STEPS[6].text]}
      index={6}
      isDisabled
    />
  )

  return (
    <div className='pre-approval-form'>
      <div className='first-section'>

        <h1>{title}</h1>
        {borrowerData &&
          <div className='text-borrower'>
            {isSomeoneElse ? (
              <>
                <strong>{`${firstName} ${lastName}`.trim() || 'The borrower'}</strong> is the borrower
              </>
            ) : (
              <>
                <DropdownOwnerships
                  items={ownerships}
                  disabled
                />
                {' '}is the borrower
              </>
            )}
          </div>
        }
        <div className='borrower'>
          {
            borrowerData ?
              null
              :
              <div>I am <OptionDropdownInner defaultValue={options[0].value} options={options} customWidth={"200px"} onChange={(val: string) => setIdentitySelection(val)} value={identitySelection} /></div>
          }
        </div>
        {/* Step indicator */}
        {!borrowerData && (
          <div className='step-indicator'>
            <span>{addressSelected ? 'STEP 1/2' : (isStep2 ? 'STEP 2/2' : 'STEP 1/2')}</span>
          </div>
        )}
        {/* Step 1: ask for property address only */}
        {!borrowerData && !isStep2 && (
          <div className='property property-step-one'>
            {!addressSelected && (
              <div className='property-address-container'>
                <span className="property-address-question">{`What's the property address?`}</span>
                <TextInput
                  error={errors.investmentAddress || undefined}
                  label={'Investment property address'}
                  alwaysShowFloatingLabel
                  value={investmentAddress}
                  floatingLabel
                >
                  <InputAutocompleteAddress
                    placeholder="Start typing an address"
                    value={investmentAddress}
                    onChange={(address: string) => {
                      setInvestmentAddress(address);
                      setHasSelectedInvestmentAddress(false);
                      setHasManuallyEditedInvestmentAddress(true);
                    }}
                    onSelect={(data: any) => {
                      const selectedAddress = data.selectedAddress || data.formatted_address;
                      const displayAddress = formatAddressWithoutCountry(selectedAddress);
                      setInvestmentAddress(displayAddress);
                      setInvestmentAddressFormatted(formatAddressWithoutCountry(data.formatted_address));
                      setHasSelectedInvestmentAddress(true);
                      setHasManuallyEditedInvestmentAddress(false);
                    }}
                    excludedStates={getDisabledStateCodes()}
                  />
                </TextInput>
              </div>
            )}
            <AddressCaptionInner {...{ addressSelected, setAddressSelected, setInvestmentAddress, setHasSelectedInvestmentAddress, setHasManuallyEditedInvestmentAddress }} />
            {!addressSelected && (
              <div className='step-actions' style={{ marginBottom: '64px' }}>
                <Button
                  outlined={false}
                  isLoading={false}
                  disabled={false}
                  onClick={() => {
                    // Validate before proceeding
                    if (!investmentAddress || investmentAddress.trim() === "") {
                      setErrors((prev) => ({ ...prev, investmentAddress: "We need this to generate your pre-approval letter and term sheet" }));
                      return;
                    }
                    if (!hasSelectedInvestmentAddress && !isInvestmentAddressPrefilled) {
                      setErrors((prev) => ({ ...prev, investmentAddress: "Please select an address from the suggestions" }));
                      return;
                    }
                    // Only proceed if address was selected from dropdown
                    if (hasSelectedInvestmentAddress || isInvestmentAddressPrefilled) {
                      setIsStep2(true);
                    }
                  }}
                >
                  Continue
                </Button>

                <a className='i-dont-have-a-specific-property' onClick={() => {
                  setInvestmentAddress("")
                  setHasSelectedInvestmentAddress(false)
                  setHasManuallyEditedInvestmentAddress(false)
                  setAddressSelected(!addressSelected)

                }}>
                  I don’t have a specific property in mind
                </a>
              </div>
            )}
            {!addressSelected && (
              <div className='resume-container'>
                <FooterContent values={values} oneLine={true} isSpan={true} firstPerson investmentAddress={investmentAddress} forceUnlockCreditInfo />
              </div>
            )}
          </div>
        )}
        {/* Step 2 (or existing flow when addressSelected): main form inputs */}
        {(borrowerData || isStep2 || addressSelected) && (
          <>
            {(!addressSelected || borrowerData || isStep2) && (!isAuthenticated || !isInvestmentAddressPrefilled) && (
              <span className='create-account-title'>
                {(isStep2 && identitySelection === 'an existing borrower' && !borrowerData)
                  ? 'Welcome back to Constitution Lending! Please enter your login details'
                  : (!isAuthenticated
                    ? (isMobile ? 'Create your account' : 'Create an account to complete your loan application')
                    : 'We need a property address to give you a term sheet. If you have one, enter it now, otherwise you can come back and start a new loan when you\'re ready.')}
              </span>
            )}
            <div className='inputs-container'>
              {/* Address-selected flow: only show Email */}
              {addressSelected && !borrowerData && (
                <div className='full-width'>
                  <TextInput
                    label={"Email address"}
                    placeholder="Enter your email address"
                    value={email}
                    onChange={w(setEmail)}
                    error={errors.email || undefined}
                    floatingLabel
                    loading={emailValidationLoading || emailCheckLoading}
                  />
                </div>
              )}

              {/* Existing borrower login form at step 2 */}
              {isStep2 && identitySelection === 'an existing borrower' && !borrowerData && (
                <>
                  <div className='full-width'>
                    <TextInput
                      label={"Email address"}
                      value={email}
                      onChange={w(setEmail)}
                      error={errors.email || undefined}
                      floatingLabel
                    />
                  </div>
                  <div className='full-width login-password-wrapper'>
                    <PasswordInput
                      label={"Password"}
                      value={password}
                      onChange={(e) => setPassword(e)}
                      error={errors.password || undefined}
                      showRequirements={false}
                      floatingLabel
                    />
                  </div>
                  <div className='full-width login-forgot'>
                    <span>Forgot password? </span>
                    <a href={`${BORROWER_DASHBOARD_URL}/reset-password`}>
                      Reset it
                    </a>
                  </div>
                </>
              )}

              {/* Normal step 2 / borrower flow: show full set */}
              {(!addressSelected || isStep2) && identitySelection !== 'an existing borrower' && !borrowerData && (

                <>
                  <TextInput label={"First name"} value={firstName} onChange={w(setFirstName)} error={errors.firstName || undefined} floatingLabel />
                  <TextInput label={"Last name"} value={lastName} onChange={w(setLastName)} error={errors.lastName || undefined} floatingLabel />
                  {!borrowerData && (
                    <>
                      <TextInput
                        label={"Email address"}
                        value={email}
                        onChange={w(setEmail)}
                        error={errors.email || undefined}
                        floatingLabel
                        loading={emailValidationLoading || emailCheckLoading}
                      />
                      <TextInput label={"Phone number"} value={phoneNumber} onChange={w((e: string) => setPhoneNumber(formatPhoneNumber(e)))} error={errors.phoneNumber || undefined} floatingLabel />
                    </>
                  )}
                  {borrowerData && (
                    <>
                      <div className='custom-select custom-select-fico'>{ficoRange}</div>
                      <div className='custom-select custom-select-properties'>{propertiesNegotiated}</div>
                    </>
                  )}
                  {shouldHidePasswordFields ? null : (
                    <>
                      <PasswordInput
                        label={"Password"}
                        value={password}
                        onChange={(e) => setPassword(e)}
                        error={errors.password || undefined}
                        showRequirements={true}
                        wrapperClassName="requirements-desktop"
                        floatingLabel
                      />
                      <PasswordInput
                        label={"Confirm Password"}
                        value={passwordConfirmation}
                        onChange={(e) => setPasswordConfirmation(e)}
                        error={errors.passwordConfirmation || undefined}
                        showRequirements={true}
                        requirementsValue={password}
                        wrapperClassName="requirements-mobile"
                        floatingLabel
                      />
                    </>
                  )}
                </>
              )}
            </div>
          </>
        )}

        {borrowerData && (
          <div className='property'>
            <TextInput error={errors.investmentAddress || undefined} value={investmentAddress}>
              <InputAutocompleteAddress
                value={investmentAddress}
                disabled={isInvestmentAddressPrefilled}
                onChange={(address: string) => {
                  setInvestmentAddress(address);
                  if (address.trim().length === 0) {
                    setHasSelectedInvestmentAddress(false);
                    setHasManuallyEditedInvestmentAddress(false);
                  } else {
                    setHasSelectedInvestmentAddress(false);
                    setHasManuallyEditedInvestmentAddress(true);
                  }
                }}
                onSelect={(data: any) => {
                  const selectedAddress = data.selectedAddress || data.formatted_address;
                  const displayAddress = formatAddressWithoutCountry(selectedAddress);
                  setInvestmentAddress(displayAddress);

                  setInvestmentAddressFormatted(formatAddressWithoutCountry(data.formatted_address));

                  setHasSelectedInvestmentAddress(true);
                  setHasManuallyEditedInvestmentAddress(false);
                }}
                excludedStates={getDisabledStateCodes()}
              />
            </TextInput>
            {!borrowerData && <AddressCaptionInner {...{ addressSelected, setAddressSelected, setInvestmentAddress, setHasSelectedInvestmentAddress, setHasManuallyEditedInvestmentAddress }} />}
          </div>
        )}


        <div>
          {
            (borrowerData || isStep2) ? (
              <>
                <div style={{ marginBottom: (isStep2 && identitySelection === 'an existing borrower' && !borrowerData) ? '89px' : '14px', marginTop: '20px' }}>
                  <Button
                    isLoading={isLoading}
                    disabled={false}
                    onClick={handleContinue}
                    onDisabledClick={isStep2 ? () => {
                      // Enable error display and show errors when clicking disabled button
                      setShouldShowErrors(true);
                      // Force check errors with shouldShowErrors = true
                      checkErrors(true);
                    } : undefined}
                  >
                    {button}
                  </Button>
                </div>
                {isAuthenticated && !isInvestmentAddressPrefilled && (
                  <div style={{ marginBottom: '14px' }}>
                    <Button
                      isLoading={false}
                      disabled={false}
                      onClick={() => window.location.reload()}
                      outlined
                      className='start-over-button'
                    >
                      Start over
                    </Button>
                  </div>
                )}
                {
                  !addressSelected ?
                    <div className='resume-container'>
                      <FooterContent values={values} oneLine={true} isSpan={true} firstPerson investmentAddress={investmentAddress} forceUnlockCreditInfo />
                    </div>
                    :
                    null
                }
              </>
            ) : (
              <>
                {
                  addressSelected && (
                    <>
                      <div style={{ marginBottom: '12px' }}>
                        <Button isLoading={isLoading} disabled={false} onClick={() => handleContinue(true)}>
                          Keep me in the loop
                        </Button>
                      </div>
                      <div style={{ marginBottom: '64px' }}>
                        <a  className='i-dont-have-a-specific-property' onClick={() => {
                          setInvestmentAddress("")
                          setHasSelectedInvestmentAddress(false)
                          setHasManuallyEditedInvestmentAddress(false)
                          setAddressSelected(!addressSelected)
                        }}>
                          I have a property address
                        </a>
                      </div>
                      {addressSelected && (
                        <div className='resume-container'>
                          <FooterContent values={values} oneLine={true} isSpan={true} firstPerson investmentAddress={investmentAddress} forceUnlockCreditInfo />
                        </div>
                      )}
                    </>
                  )
                }
              </>
            )
          }
          <span className='caption'>
            We will NEVER sell your info.<br></br>
            By providing your phone and email, you agree to receive text messages from Constitution Lending.
          </span>
        </div>

      </div>
    </div>
  );
}

export const AddressCaptionInner = (props: any) => {
  const {
    addressSelected,
    setAddressSelected,
    setInvestmentAddress,
    caption = "I don't have specific property in mind",
    setHasSelectedInvestmentAddress,
    setHasManuallyEditedInvestmentAddress,
  } = props;
  const addressCopy = "We need a property address to give you a term sheet, but we’d love to send you updates on what we’re doing"

  const { isAuthenticated } = useAuth();

  const handleOnClick = () => {
    setInvestmentAddress("")
    setHasSelectedInvestmentAddress?.(false)
    setHasManuallyEditedInvestmentAddress?.(false)
    setAddressSelected(!addressSelected)
  }

  // Hide the "I don't have specific property in mind" option if user is logged in
  if (isAuthenticated) {
    return null
  }

  return (
    <React.Fragment>
      {/*       <p
        className={addressSelected ? 'address-short-caption selected' : 'address-short-caption'}
        onClick={handleOnClick}>
        {caption}
      </p> */}
      {
        addressSelected ?
          <p className='address-caption'>{addressCopy}</p>
          :
          null
      }
    </React.Fragment>
  )
}

export const AddressCaption = () => {
  const [addressSelected, setAddressSelected] = useState(false)

  return (
    <AddressCaptionInner {...{ addressSelected, setAddressSelected }} />
  )
}

export const Button = ({
  children,
  onClick,
  onDisabledClick,
  disabled,
  isLoading,
  outlined = false,
  className,
}: {
  children: React.ReactNode;
  onClick: () => void;
  onDisabledClick?: () => void;
  disabled: boolean;
  isLoading: boolean;
  outlined?: boolean;
  className?: string;
}) => {
  // Ensure disabled is a boolean
  const isDisabled = Boolean(disabled);

  // Add 'disabled' class when button is disabled to show gray appearance
  const buttonClassName = ['loan-options-button', outlined ? 'outlined' : '', isDisabled ? 'disabled' : '', className]
    .filter(Boolean)
    .join(' ');

  const handleClick = (e: React.MouseEvent) => {
    // If disabled, only execute onDisabledClick if provided, otherwise do nothing
    if (isDisabled) {
      e.preventDefault();
      e.stopPropagation();
      if (onDisabledClick) {
        onDisabledClick();
      }
      return;
    }
    // Only execute onClick if not disabled
    onClick();
  };

  // If disabled and no onDisabledClick, completely block pointer events
  const shouldBlockPointerEvents = isDisabled && !onDisabledClick;

  return (
    <div
      className={buttonClassName}
      onClick={shouldBlockPointerEvents ? undefined : handleClick}
      style={shouldBlockPointerEvents ? { pointerEvents: 'none', cursor: 'not-allowed' } : (isDisabled ? { cursor: 'not-allowed' } : { cursor: 'pointer' })}
      onMouseDown={(e) => {
        if (isDisabled && !onDisabledClick) {
          e.preventDefault();
          e.stopPropagation();
        }
      }}
    >
      {children}
      {
        isLoading && (
          <span style={{ marginLeft: '8px' }}>
            {outlined ? <BlueSpinner /> : <WhiteSpinner />}
          </span>
        )
      }
    </div>
  );
};